/**
 * Buscador de productos, licencias y servicios para añadir líneas.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 15-sep-2026: "un buscador de la parte de productos, licencias y
 * servicios, porque la lista es grande y es poco usable". Lo que había eran
 * dos desplegables encadenados: primero el tipo, luego el producto entre
 * todos los de ese tipo, sin poder escribir.
 *
 * Aquí se escribe y se filtra al momento. El catálogo ya está cargado
 * (`GET /products` se pide al abrir el diálogo), así que no hay peticiones:
 * al contrario que `ClientSearchSelect`, que pregunta al servidor porque son
 * 2.000 clientes.
 *
 * Al elegir uno se llama a `onPick(producto)` y el cuadro se vacía, listo para
 * el siguiente: lo normal es añadir varias líneas seguidas.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { etiquetaDeTipo, filtrarProductos } from '@/lib/productos';

const euros = (n) => `${Number(n || 0).toFixed(2)} €`;

export function ProductSearchSelect({ products, onPick, placeholder }) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [marcado, setMarcado] = useState(0);
  const contenedor = useRef(null);

  const resultados = useMemo(() => filtrarProductos(products, texto), [products, texto]);

  // Al cambiar lo escrito, el primero de la lista es el que se llevaría Enter.
  useEffect(() => { setMarcado(0); }, [texto]);

  // Clic fuera: se cierra. Sin esto la lista tapaba la tabla de líneas.
  useEffect(() => {
    const fuera = (e) => {
      if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const elegir = (producto) => {
    onPick?.(producto);
    setTexto('');
    setAbierto(false);
  };

  const teclas = (e) => {
    // Con la lista cerrada, la flecha abajo la abre: es el gesto de siempre
    // en un desplegable y la salida para quien no sabe qué hay en el
    // catálogo. Lo importante es que la pide el usuario a propósito.
    //
    // Y Enter NO hace nada aquí. Antes, con el cuadro vacío la lista estaba
    // abierta con el catálogo entero, así que Enter añadía el primer producto
    // que hubiera — uno cualquiera.
    if (!abierto) {
      if (e.key === 'ArrowDown' && resultados.length) {
        e.preventDefault();
        setAbierto(true);
      }
      return;
    }
    if (!resultados.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMarcado((i) => Math.min(i + 1, resultados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMarcado((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      elegir(resultados[marcado]);
    } else if (e.key === 'Escape') {
      setAbierto(false);
    }
  };

  return (
    <div ref={contenedor} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={texto}
          // La lista se abre porque hay ALGO QUE BUSCAR, no por enfocar el
          // cuadro. Enfocando se volcaba el catálogo entero -- 40 productos,
          // hasta 288 px en `absolute` -- justo encima de la tabla de líneas,
          // y el clic que iba al concepto de una línea caía sobre un producto
          // y lo añadía (Angel, 28-sep-2026: "se añade solo un nuevo producto
          // debajo del que he añadido yo").
          //
          // Borrar lo escrito la cierra por lo mismo: si no, quedaba abierta
          // con el catálogo entero y volvíamos al mismo sitio.
          onChange={(e) => {
            setTexto(e.target.value);
            setAbierto(e.target.value.trim() !== '');
          }}
          onKeyDown={teclas}
          placeholder={placeholder || 'Buscar producto, licencia o servicio…'}
          className="pl-9"
          data-testid="buscar-producto"
          autoComplete="off"
        />
      </div>

      {abierto && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border bg-white shadow-lg">
          {resultados.length === 0 ? (
            <p className="p-3 text-sm text-slate-500">
              Nada con &quot;{texto}&quot;. Prueba con otra palabra o con la referencia.
            </p>
          ) : (
            resultados.map((p, i) => {
              const tipo = etiquetaDeTipo(p.type);
              return (
                <button
                  type="button"
                  key={p.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => elegir(p)}
                  onMouseEnter={() => setMarcado(i)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-3 text-sm
                    ${i === marcado ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                  data-testid={`producto-${p.id}`}
                >
                  <span className="shrink-0" title={tipo.nombre}>{tipo.emoji}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">
                      {p.ref ? <span className="text-slate-500 mr-1">{p.ref}</span> : null}
                      {p.name}
                    </span>
                    {/* Solo la primera linea: la descripcion puede traer
                        varias y aqui es una pista, no el texto entero. */}
                    {p.description ? (
                      <span className="block text-xs text-slate-500 truncate">
                        {String(p.description).split('\n')[0]}
                      </span>
                    ) : null}
                  </span>
                  {/* Impuesto propio: se ve antes de añadirlo, para que un 0%
                      no pase desapercibido en la factura. */}
                  {p.tax_rate !== null && p.tax_rate !== undefined && (
                    <span className="shrink-0 text-[11px] text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
                      {p.tax_rate === 0 ? 'sin imp.' : `${p.tax_rate}%`}
                    </span>
                  )}
                  <span className="shrink-0 font-medium">{euros(p.price)}</span>
                </button>
              );
            })
          )}
          {resultados.length >= 40 && (
            <p className="p-2 text-xs text-slate-400 border-t">
              Se enseñan los 40 primeros. Escribe algo más para afinar.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductSearchSelect;
